import { getProductInfo, productSync } from '@/api';
import { getGbsip, postGbsip } from '@/api/devices';
import { AdvanceBreadcrumb } from '@/components/Breadcrumb';
import useAuthButton from '@/hooks/useAuthButton';
import { SyncOutlined } from '@ant-design/icons';
import { Button, Card, Col, Descriptions, Form, Input, InputNumber, message, Row, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import DataModal, { DataModalRef } from './DataModal';
import { DetailModal, DetailModalRef } from './DeatilModal';
import { DefaultDetail as V2Detail } from './V2Detail';
import { ModbusDetail } from './ModbusDetail';
import { LuaScriptModal } from './components/ProductLua';
import './style.less';
import { ProductInfo } from './types';

const { useMessage } = message;

const SIPFormCard = ({ productId }: { productId: string }) => {
  const [form] = Form.useForm();

  const [, setSubmittable] = React.useState<boolean>(false);

  // Watch all values
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  const [loading, setLoading] = useState<boolean>(false);

  const getData = async (id: string) => {
    setLoading(true);
    try {
      const res = await getGbsip(id);
      if (res.success) {
        form.setFieldsValue(res.result);
      } else {
        message.error(res.errorMsg);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData(productId);
  }, [productId]);

  const [, setSpinning] = useState<boolean>(false);
  const onFinish = async (data: any) => {
    setSpinning(true);
    try {
      const res = await postGbsip(data);
      if (res.success) {
        message.success('提交成功');
      } else {
        message.error(res.errorMsg);
      }
    } catch {
    } finally {
      setSpinning(false);
    }
  };

  return (
    <Card className="m20" title="SIP信息" loading={loading}>
      <Form layout="horizontal" form={form} requiredMark={false} onFinish={onFinish} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
        <Row>
          <Col span={12}>
            <Form.Item name="id" noStyle>
              <div></div>
            </Form.Item>
            <Form.Item name="addr" label="服务器地址" rules={[{ required: true, message: '请输入服务器地址' }]}>
              <Input placeholder="请输入服务器地址" readOnly></Input>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="port" label="服务器端口" rules={[{ required: true, message: '请输入服务器端口' }]}>
              <InputNumber placeholder="请输入服务器端口" readOnly width={'100%'} style={{ width: '100%' }}></InputNumber>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="server_region" label="服务器域" rules={[{ required: true, message: '请输入服务器域' }]}>
              <Input placeholder="请输入服务器域" readOnly></Input>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="server_id" label="服务器ID" rules={[{ required: true, message: '请输入服务器ID' }]}>
              <Input placeholder="请输入服务器ID" readOnly></Input>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="password" label="认证密码" rules={[{ required: true, message: '请输入认证密码' }]}>
              <Input.Password placeholder="请输入认证密码" readOnly></Input.Password>
            </Form.Item>
          </Col>
          {/* <Col span={12}></Col>
          <Col span={12}>
            <Form.Item label="" wrapperCol={{ offset: 6 }}>
              <Space size={24}>
                <Button htmlType="submit" type="primary" disabled={!submittable} loading={spinning}>
                  提交
                </Button>
              </Space>
            </Form.Item>
          </Col> */}
        </Row>
      </Form>
    </Card>
  );
};

const ProductDetail = () => {
  const { checkPermissionDisabled } = useAuthButton();
  const params = useParams<{ id: string }>();
  const deatilModalRef = useRef<DetailModalRef>(null);
  const [, contextHolder] = useMessage();

  const [submitting, setSubmitting] = useState(false);
  const [productInfo, setProductInfo] = useState<ProductInfo>();
  const [luaScriptVisible, setLuaScriptVisible] = useState(false);

  const isCloudProduct = useMemo(() => productInfo?.cloud_instance_id, [productInfo]);

  const loadProductInfo = useCallback(() => {
    console.log('[产品详情] 加载产品信息, productId:', params.id);
    getProductInfo<ProductInfo>(params.id!).then((resp) => {
      console.log('[产品详情] API响应:', resp);
      if (resp.success) {
        console.log('[产品详情] 属性数:', resp.result?.properties?.length, '事件数:', resp.result?.events?.length, '服务数:', resp.result?.actions?.length);
        console.log('[产品详情] 产品数据:', resp.result);
        setProductInfo(resp.result);
      } else {
        console.error('[产品详情] 加载失败:', resp.errorCode, resp.errorMsg);
      }
    }).catch((e) => {
      console.error('[产品详情] 请求异常:', e);
    });
  }, [params]);

  useEffect(() => {
    loadProductInfo();
  }, [params, loadProductInfo]);

  const dataModalRef = useRef<DataModalRef>(null);

  useEffect(() => {
    localStorage.setItem('title', '产品详情');
    document.title = '产品详情';
  }, []);

  const isMonitor = productInfo?.node_type === '监控设备';

  return (
    <>
      {contextHolder}
      <DetailModal
        ref={deatilModalRef}
        productInfo={productInfo}
        onSuccess={() => {
          loadProductInfo();
        }}
      ></DetailModal>
      <DataModal
        ref={dataModalRef}
        onSuccess={() => {
          loadProductInfo();
        }}
      ></DataModal>
      <AdvanceBreadcrumb
        hasBack
        title="产品管理"
        describe="产品物模型是对同一类设备的数字化抽象描述，描述该类设备是什么，能做什么，能对外提供哪些服务，它包括属性、事件、服务三种功能类型。"
        background={require('@/assets/images/product-background.png')}
        breadcrumb={[
          { label: '产品管理', to: '/gateway/product/manage' },
          { label: `产品详情 [ ${productInfo?.name || ''} ]`, to: '' },
        ]}
      />
      <Card
        // title={
        //   <>
        //     {productInfo?.name}
        //     <Popconfirm
        //       title={productInfo?.status === '已发布' ? '是否确认取消发布？' : '是否确认发布？'}
        //       okText="确认"
        //       cancelText="取消"
        //       disabled={checkPermissionDisabled('gateway:product:tsl:release')}
        //       onConfirm={() => {
        //         if (productInfo?.status === '已发布') {
        //           productUnrelease(productInfo?.id || '').then((resp) => {
        //             if (resp.success) {
        //               messageApi.open({ type: 'success', content: '取消发布成功' });
        //               loadProductInfo();
        //             } else {
        //               messageApi.open({ type: 'error', content: resp.errorMsg });
        //             }
        //           });
        //         } else {
        //           productRelease(productInfo?.id || '').then((resp) => {
        //             if (resp.success) {
        //               messageApi.open({ type: 'success', content: '发布成功' });
        //               loadProductInfo();
        //             } else {
        //               messageApi.open({ type: 'error', content: resp.errorMsg });
        //             }
        //           });
        //         }
        //       }}
        //     >
        //       <Button className="ml20" size="small" type="primary">
        //         {productInfo?.status === '已发布' && (
        //           <Space>
        //             <CheckCircleFilled style={{ color: '#55af70' }} />
        //             已发布
        //           </Space>
        //         )}
        //         {productInfo?.status === '未发布' && (
        //           <Space>
        //             <MinusCircleFilled style={{ color: '#87909d' }} />
        //             未发布
        //           </Space>
        //         )}
        //       </Button>
        //     </Popconfirm>
        //   </>
        // }
        style={{ margin: '-15px 20px 20px' }}
      >
        <Descriptions
          column={3}
          size="small"
          bordered
          extra={
            isCloudProduct && (
              <Button
                type="link"
                size="small"
                loading={submitting}
                disabled={checkPermissionDisabled('gateway:product:sync') || submitting}
                icon={<SyncOutlined />}
                style={{ fontSize: 12 }}
                onClick={() => {
                  setSubmitting(true);
                  productSync({ product_id: params.id! })
                    .then((resp) => {
                      if (resp.success) {
                        message.success('同步成功');
                        loadProductInfo();
                      } else {
                        message.error(resp.errorMsg);
                      }
                    })
                    .finally(() => {
                      setSubmitting(false);
                    });
                }}
              >
                同步产品
              </Button>
            )
          }
        >
          <Descriptions.Item label="产品ID">{productInfo?.id}</Descriptions.Item>
          <Descriptions.Item label="产品名称">{productInfo?.name}</Descriptions.Item>
          <Descriptions.Item label="产品标识">{productInfo?.key}</Descriptions.Item>
          <Descriptions.Item label="产品类型">{productInfo?.node_type}</Descriptions.Item>
          <Descriptions.Item label="品牌名称">{productInfo?.brand}</Descriptions.Item>
          <Descriptions.Item label="产品状态">{productInfo?.status}</Descriptions.Item>
          <Descriptions.Item label="是否为IPC产品">{productInfo?.is_ipc ? '是' : '否'}</Descriptions.Item>
          <Descriptions.Item label="接入协议驱动">
            <Tag>{productInfo?.drive_instance_name}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{dayjs(productInfo?.created_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{dayjs(productInfo?.update_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
          <Descriptions.Item label="协议解析">
            <Button type="link" size="small" onClick={() => setLuaScriptVisible(true)}>
              Lua脚本
            </Button>
          </Descriptions.Item>
          <Descriptions.Item label="描述" span={3}>
            {productInfo?.description}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      {/* <Card className="product-info">
        <div className="product-info__body">
          <img src={require('@/assets/images/default.32752796.png')} alt="" />
          <div>
            <Descriptions
              className="product-info__descriptions"
              title={
                <>
                  {productInfo?.name}
                  <Popconfirm
                    title={productInfo?.status === '已发布' ? '是否确认取消发布？' : '是否确认发布？'}
                    okText="确认"
                    cancelText="取消"
                    disabled={checkPermissionDisabled('gateway:product:tsl:release')}
                    onConfirm={() => {
                      if (productInfo?.status === '已发布') {
                        productUnrelease(productInfo?.id || '').then((resp) => {
                          if (resp.success) {
                            messageApi.open({ type: 'success', content: '取消发布成功' });
                            loadProductInfo();
                          } else {
                            messageApi.open({ type: 'error', content: resp.errorMsg });
                          }
                        });
                      } else {
                        productRelease(productInfo?.id || '').then((resp) => {
                          if (resp.success) {
                            messageApi.open({ type: 'success', content: '发布成功' });
                            loadProductInfo();
                          } else {
                            messageApi.open({ type: 'error', content: resp.errorMsg });
                          }
                        });
                      }
                    }}
                  >
                    <Button className="ml20" size="small">
                      {productInfo?.status === '已发布' && (
                        <Space>
                          <CheckCircleFilled style={{ color: '#55af70' }} />
                          已发布
                        </Space>
                      )}
                      {productInfo?.status === '未发布' && (
                        <Space>
                          <MinusCircleFilled style={{ color: '#87909d' }} />
                          未发布
                        </Space>
                      )}
                    </Button>
                  </Popconfirm>
                </>
              }
              column={2}
              extra={
                isCloudProduct && (
                  <Button
                    type="link"
                    size="small"
                    loading={submitting}
                    disabled={checkPermissionDisabled('gateway:product:sync') || submitting}
                    icon={<SyncOutlined />}
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      setSubmitting(true);
                      productSync({ product_id: params.id! })
                        .then((resp) => {
                          if (resp.success) {
                            message.success('同步成功');
                            loadProductInfo();
                          } else {
                            message.error(resp.errorMsg);
                          }
                        })
                        .finally(() => {
                          setSubmitting(false);
                        });
                    }}
                  >
                    同步产品
                  </Button>
                )
              }
              styles={{ label: { fontSize: 12, color: '#8a949c' }, content: { fontSize: 12 } }}
            >
              <Descriptions.Item label="产品ID">
                <Text copyable>{productInfo?.id}</Text>
              </Descriptions.Item>
              {!isMonitor && (
                <>
                  {isCloudProduct && (
                    <Descriptions.Item label="云端产品标识">
                      <Text copyable>{productInfo?.cloud_product_id}</Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="节点类型">{productInfo?.node_type}</Descriptions.Item>
                  <Descriptions.Item label="数据来源">{productInfo?.platform}</Descriptions.Item>
                  <Descriptions.Item label="数据格式">{productInfo?.data_format}</Descriptions.Item>
                  <Descriptions.Item label="协议类型">{productInfo?.protocol}</Descriptions.Item>
                  <Descriptions.Item label="创建时间">{dayjs(productInfo?.created_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
                  <Descriptions.Item label="厂商名称">{productInfo?.factory}</Descriptions.Item>
                  {isCloudProduct && <Descriptions.Item label="最后一次同步时间">{dayjs(productInfo?.last_sync_time).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>}
                  <Descriptions.Item label="描述" span={isCloudProduct ? 3 : 2}>{productInfo?.description}</Descriptions.Item>
                </>
              )}
            </Descriptions>
          </div>
        </div>
      </Card> */}

      {!isMonitor &&
        (productInfo?.protocol === 'ModbusTCP' || productInfo?.protocol === 'ModbusRTU' ? (
          <ModbusDetail productId={params.id} />
        ) : (
          <V2Detail productInfo={productInfo} isCloudProduct={!!isCloudProduct} loadProductInfo={loadProductInfo} productId={params.id!} />
        ))}
      {isMonitor && <SIPFormCard productId={params.id!}></SIPFormCard>}
      <LuaScriptModal
        visible={luaScriptVisible}
        product_id={productInfo?.id || ''}
        initialValue={productInfo?.lua_script}
        initialEnabled={productInfo?.lua_script_enable || false}
        onCancel={() => setLuaScriptVisible(false)}
        onSubmit={() => {
          setLuaScriptVisible(false);
          loadProductInfo();
        }}
      />
    </>
  );
};

export default ProductDetail;
